import { useEffect, useState, useContext } from 'react'
import type { JSX } from 'react'

import {
  Heading,
  Button,
  IconButton,
  XIcon,
  ChannelIcon,
  FacebookIcon,
  InstagramIcon,
} from '@buffer-mono/popcorn'

import { getActiveProductFromPath } from '../../../../common/utils/getProduct'
import { useTrackPageViewed } from '../../../../common/hooks/useSegmentTracking'
import { UserContext } from '../../../../common/context/User'

import { Content, Icons, CloseBtnWrapper, ConnectButtons } from './style'
import { Service } from '../../../../exports/Orchestrator/channelConnections/types'
import { ORCHESTRATOR_ACTIONS } from '../../../../common/events/orchestratorEvents'

export { SUPPORTED_PRODUCTS } from './constants'

function renderCloseBtn(onClose: () => void): JSX.Element {
  return (
    <CloseBtnWrapper>
      <IconButton
        variant="tertiary"
        label="Close"
        onClick={(): void => onClose()}
      >
        <XIcon />
      </IconButton>
    </CloseBtnWrapper>
  )
}

const CTASection = ({
  label = 'Connect a Channel',
}: {
  label?: string
}): JSX.Element => {
  function handleCTAClick(): void {
    ORCHESTRATOR_ACTIONS.openChannelStorefront({
      cta: `analyze-connect-channel-prompt-cta`,
    })
  }
  return (
    <Button variant="primary" onClick={handleCTAClick}>
      {label}
    </Button>
  )
}

const Analytics = (args: { onClose: () => void }): JSX.Element => (
  <>
    {renderCloseBtn(args.onClose)}
    <Heading as="h3" size="sm">
      Connect a channel <br /> to get started with Analytics
    </Heading>
    <Icons>
      <ChannelIcon type="facebook" color="branded" size="large" />
      <ChannelIcon type="instagram" color="branded" size="large" />
      <ChannelIcon type="twitter" color="branded" size="large" />
      <ChannelIcon type="linkedin" color="branded" size="large" />
    </Icons>
    <img
      src="https://buffer-ui.s3.amazonaws.com/onboarding/connection-modal-analytics_%40x2.jpg"
      alt="A preview of Buffer's Analytics app."
      width="313px"
    />
    <ConnectButtons>
      <CTASection />
    </ConnectButtons>
  </>
)

const Engagement = (args: { onClose: () => void }): JSX.Element => {
  return (
    <>
      {renderCloseBtn(args.onClose)}
      <Heading as="h3" size="sm">
        Connect a channel <br /> to get started with Engagement
      </Heading>
      <Icons>
        <ChannelIcon type="facebook" color="branded" size="large" />
        <ChannelIcon type="instagram" color="branded" size="large" />
      </Icons>
      <img
        src="https://buffer-ui.s3.amazonaws.com/onboarding/connection-modal-engagement_%40x2.jpg"
        alt="A preview of Buffer's Engagement app."
        width="313px"
      />
      <ConnectButtons>
        <Button
          variant="primary"
          onClick={(): void => {
            if (window.appshell?.actions) {
              const { actions } = window.appshell || {}
              actions.connectChannel({
                selectedService: Service.facebook,
                cta: `engange-channelPrompt-channelConnect-facebook-1`,
              })
            }
          }}
        >
          Connect Facebook
          <FacebookIcon />
        </Button>
        <Button
          variant="primary"
          onClick={(): void => {
            if (window.appshell?.actions) {
              const { actions } = window.appshell || {}
              actions.connectChannel({
                selectedService: Service.instagram,
                cta: `engange-channelPrompt-channelConnect-instagram-1`,
              })
            }
          }}
        >
          Connect Instagram
          <InstagramIcon />
        </Button>
      </ConnectButtons>
    </>
  )
}

const Publish = (args: { onClose: () => void }): JSX.Element => (
  <>
    {renderCloseBtn(args.onClose)}
    <Heading as="h3" size="sm">
      Connect a channel <br /> to get started with Publishing
    </Heading>
    <Icons>
      <ChannelIcon type="tiktok" color="branded" size="large" />
      <ChannelIcon type="googlebusiness" color="branded" size="large" />
      <ChannelIcon type="facebook" color="branded" size="large" />
      <ChannelIcon type="instagram" color="branded" size="large" />
      <ChannelIcon type="twitter" color="branded" size="large" />
      <ChannelIcon type="linkedin" color="branded" size="large" />
      <ChannelIcon type="pinterest" color="branded" size="large" />
    </Icons>
    <img
      src="https://buffer-ui.s3.amazonaws.com/connection_modal_publishing.png"
      alt="A preview of BUffer's Publishing app."
      width="313px"
    />
    <ConnectButtons>
      <CTASection />
    </ConnectButtons>
  </>
)

const ChannelConnectionPrompt = (args: {
  closeAction: () => void
}): JSX.Element => {
  const [product, setProduct] = useState('')
  const user = useContext(UserContext)
  const { closeAction } = args

  useEffect(() => {
    const activeProduct = getActiveProductFromPath()
    setProduct(activeProduct)

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useTrackPageViewed({
      payload: {
        name: 'Connect Channel Prompt',
        title: 'Connect a Channel',
        product: activeProduct,
      },
      user,
    })
  }, [])

  return (
    <Content id="channel-connection-prompt">
      {product === 'analyze' && <Analytics onClose={closeAction} />}
      {product === 'engage' && <Engagement onClose={closeAction} />}
      {product !== 'analyze' && product !== 'engage' && (
        <Publish onClose={closeAction} />
      )}
    </Content>
  )
}

export default ChannelConnectionPrompt
